import { FormEvent, KeyboardEvent, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { AutoCompleteSelectEvent } from 'primereact/autocomplete';
import { Button } from 'primereact/button';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { Steps } from 'primereact/steps';
import { classNames } from 'primereact/utils';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { addToast } from '@/app/slices/toast';
import {
  AccountAutoComplete,
  BreedsAutoComplete,
  defaultGradingRows,
  GradingGrid,
  GradingGridRow,
  gridToStageGradeCount,
  OptionSetsDropdown,
  SitesAutoComplete,
  StorageLocationAutoComplete,
  StringMapDropdown,
  YardsAutoComplete,
} from '@/components';
import { AdvancedAccountLookup } from '@/components/common/advanced-account-lookup';
import { AdvancedDonorLookup } from '@/components/common/advanced-donor-lookup';
import { AdvancedSireLookup } from '@/components/common/advanced-sire-lookup';
import { CalendarWrapper as Calendar } from '@/components/common/calendar-wrapper';
import { sumGradingRows } from '@/components/common/grading-grid/utils';
import { StorageLocationCombobox } from '@/components/common/storage-location-combobox';
import { StringMapObjectTypeCode } from '@/components/common/string-map-dropdown';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import { EMBRYO_METHOD_OPTIONS, FRESH_FROZEN, SEX_EMBRYO } from '@/constants';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import type { Donor, Sire, StorageLocation } from '@/rtk-query.gql';
import type { AccountResponse } from '@/services/accounts';
import { useAddOutsideEmbryosV2 } from '@/services/embryo-service/hooks';
import { runNewWorkflow, WORKFLOW } from '@/utils/workflow';

import {
  DEFAULT_VALUES,
  FRESH_FROZEN_OPTIONS,
  LOAD_DONOR_PARTNERS_OPTIONS,
  STEPS,
  STEPS_V2,
  TRANSFER_CLINIC_RANCH_HAULIN_OPTIONS,
} from './constants';
import { outsideEmbryoFormSchemaV2, OutsideEmbryoFormValuesV2 } from './schema';
import { CreateOutsideEmbryosWizardProps } from './types';

const sexOptions = Object.entries(SEX_EMBRYO).map(([label, value]) => ({
  label,
  value,
}));

export const CreateOutsideEmbryosWizard = ({
  initialValues,
  onCancel,
  onSuccess,
  onError,
}: CreateOutsideEmbryosWizardProps) => {
  const isOutsideEmbryoUpdatesEnabled = useFeatureFlag(
    'Outside_Embryo_Updates',
  );
  const isEmbryoOwnerAlternateSearchEnabled = useFeatureFlag(
    'Alternate_Account_Lookup',
  );
  const isStorageLocationPagingEnabled = useFeatureFlag(
    'Storage_Location_Paging',
  );
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [activeIndex, setActiveIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const steps = isOutsideEmbryoUpdatesEnabled ? STEPS_V2 : STEPS;
  const [gradingGridData, setGradingGridData] = useState<GradingGridRow[]>(() =>
    defaultGradingRows.map((r) => ({ ...r })),
  );
  const [implantGradingGridData, setImplantGradingGridData] = useState<
    GradingGridRow[]
  >(() => defaultGradingRows.map((r) => ({ ...r })));

  const { mutateAsync: addOutsideEmbryosV2 } = useAddOutsideEmbryosV2();

  const {
    control,
    handleSubmit,
    reset,
    trigger,
    watch,
    setValue,
    formState: { errors },
  } = useForm<OutsideEmbryoFormValuesV2>({
    resolver: zodResolver(outsideEmbryoFormSchemaV2),
    defaultValues: DEFAULT_VALUES,
  });
  const embryoOwnerId = watch('freezeOwnerId');
  const transferOwnerId = watch('transferOwnerId');

  const freshFrozenValue = watch('freshFrozen');
  const isFresh = freshFrozenValue === FRESH_FROZEN.Fresh;

  const getFormErrorMessage = (name: keyof OutsideEmbryoFormValuesV2) => {
    return (
      errors[name] && (
        <small className="mt-1 text-sm text-red-500">
          {errors[name]?.message}
        </small>
      )
    );
  };

  const handleCancel = () => {
    reset();
    onCancel();
  };

  const handleNext = async () => {
    const fieldsToValidate: (keyof OutsideEmbryoFormValuesV2)[] = [];

    if (activeIndex === 0) {
      fieldsToValidate.push(
        'donorId',
        'sireId',
        'freezeOwnerId',
        'freshFrozen',
      );
    } else if (activeIndex === 1) {
      fieldsToValidate.push();
    } else if (activeIndex === 2) {
      fieldsToValidate.push();
    }

    const isValid = await trigger(fieldsToValidate as any);

    if (isValid && activeIndex < steps.length - 1) {
      setActiveIndex(activeIndex + 1);
    }
  };

  const handleBack = () => {
    if (activeIndex > 0) {
      setActiveIndex(activeIndex - 1);
    }
  };

  const onSubmit = async (data: OutsideEmbryoFormValuesV2) => {
    if (isSubmitting) return;
    if (activeIndex !== steps.length - 1) return;

    try {
      setIsSubmitting(true);
      const transformedInput = {
        donorId: data.donorId?.id,
        sireId: data.sireId?.id,
        freezeOwnerId: data.freezeOwnerId?.id,
        transferOwnerId: data.transferOwnerId?.id,
        breedId: data.breedId?.id,
        method: data.method,
        recoveryFirmCode: data.recoveryFirmCode,
        recoveryFirmName: data.recoveryFirmName,
        freshFrozen: data.freshFrozen,
        freezeCane: data.freezeCane,
        creation: data.creation?.toISOString(),
        freezeTechName: data.freezeTechName,
        freezeMethodId: data.freezeMethodId,
        implantDate: data.implantDate?.toISOString(),
        transferQuantity: data.transferQuantity,
        thawByName: data.thawByName,
        implantTierOf: data.implantTierOf,
        transferSiteId: data.transferSiteId?.id,
        transferClinicRanchHaulIn: data.transferClinicRanchHaulIn,
        transferTechName: data.transferTechName,
        implantType: data.implantType,
        recipientOwnerId: data.recipientOwnerId?.id,
        deyardId: data.deyardId?.id,
        freezeQuantity: data.freezeQuantity,
        freezeStorageLocationId: data.freezeStorageLocationId?.id,
        receivedOn: data.receivedOn?.toISOString(),
        freezeTt: data.freezeTt,
        freezer: data.freezer,
        freezeTime: data.freezeTime,
        sex: data.sex,
        stage: data.stage,
        grade: data.grade,
        freezeEmbryoStageGradeCount: gridToStageGradeCount(gradingGridData),
        implantedEmbryoStageGradeCount: gridToStageGradeCount(
          implantGradingGridData,
        ),
      };
      const res = await addOutsideEmbryosV2(transformedInput);
      if (res?.success === false) {
        dispatch(
          addToast({
            severity: 'warn',
            summary: t('common.error'),
            detail: res.message || t('embryo.failedToCreateOutsideEmbryo'),
            life: ToastLife.MEDIUM,
          }),
        );
      } else {
        if (isOutsideEmbryoUpdatesEnabled) {
          const freezeIds: string[] = res?.createdFreezeEntryIds ?? [];
          const implantIds: string[] = res?.createdImplantEntryIds ?? [];

          if (freezeIds.length > 0) {
            runNewWorkflow(
              freezeIds.map((id: string) => ({ RecordId: id })),
              WORKFLOW.CreateEmbryoFreeze.id,
            ).catch(() => {
              dispatch(
                addToast({
                  severity: 'warn',
                  summary: 'Workflow Warning',
                  detail:
                    'Embryos created but Create Embryo (Freeze) workflow failed to start.',
                  life: ToastLife.LONG,
                }),
              );
            });
          }
          if (implantIds.length > 0) {
            runNewWorkflow(
              implantIds.map((id: string) => ({ RecordId: id })),
              WORKFLOW.CreateEmbryoImplant.id,
            ).catch(() => {
              dispatch(
                addToast({
                  severity: 'warn',
                  summary: 'Workflow Warning',
                  detail:
                    'Embryos created but Create Embryo (Implant) workflow failed to start.',
                  life: ToastLife.LONG,
                }),
              );
            });
          }
        }
        dispatch(
          addToast({
            severity: 'success',
            summary: t('common.success'),
            detail: t('embryo.outsideEmbryoCreatedSuccessfully'),
            life: ToastLife.MEDIUM,
          }),
        );
        onSuccess?.(data);
        reset();

        if (isOutsideEmbryoUpdatesEnabled && res?.outsideEmbryo?.id) {
          const outsideEmbryoId = res.outsideEmbryo.id;
          const hasImplants = (res.createdImplantEntryIds ?? []).length > 0;
          const hasFreezes = (res.createdFreezeEntryIds ?? []).length > 0;

          let tab = 'implant';
          if (hasImplants) {
            tab = 'implant';
          } else if (hasFreezes) {
            tab = 'freezing';
          }

          navigate(
            `/embryo/embryo?id=${outsideEmbryoId}&type=outside&tab=${tab}`,
          );
        } else {
          onCancel();
        }
      }
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : t('embryo.failedToCreateOutsideEmbryo');
      dispatch(
        addToast({
          severity: 'error',
          summary: t('common.error'),
          detail: message,
          life: ToastLife.MEDIUM,
        }),
      );
      onError?.(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderGeneralStep = () => (
    <div className="bg-white p-6">
      <h3 className="mb-6 text-lg font-bold text-blue-600">
        General Information
      </h3>
      <div className="grid grid-cols-3 gap-6">
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">
            <div className="flex items-center gap-1">
              <span>Donor</span>
              <span className="text-red-500">*</span>
            </div>
          </label>
          <Controller
            name="donorId"
            control={control}
            render={({ field, fieldState }) => (
              <AdvancedDonorLookup
                label={undefined}
                value={field.value as Donor}
                className={classNames('w-full text-sm', {
                  'p-invalid': fieldState.invalid,
                })}
                onChange={(donor) => {
                  field.onChange(donor);
                }}
                appendTo={undefined}
              />
            )}
          />
          {getFormErrorMessage('donorId')}
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">
            <div className="flex items-center gap-1">
              <span>Sire</span>
              <span className="text-red-500">*</span>
            </div>
          </label>
          <Controller
            name="sireId"
            control={control}
            render={({ field, fieldState }) => (
              <AdvancedSireLookup
                label={undefined}
                value={field.value as Sire}
                className={classNames('w-full text-sm', {
                  'p-invalid': fieldState.invalid,
                })}
                onChange={(sire) => field.onChange(sire)}
                showEditButton={false}
                showAddButton={false}
                appendTo={undefined}
              />
            )}
          />
          {getFormErrorMessage('sireId')}
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">
            <div className="flex items-center gap-1">
              <span>Embryo Owner</span>
              <span className="text-red-500">*</span>
            </div>
          </label>
          <Controller
            name="freezeOwnerId"
            control={control}
            render={({ field, fieldState }) =>
              isEmbryoOwnerAlternateSearchEnabled ? (
                <AdvancedAccountLookup
                  value={field.value as AccountResponse | null}
                  className={classNames('w-full text-sm', {
                    'p-invalid': fieldState.invalid,
                  })}
                  onChange={(account) => field.onChange(account)}
                />
              ) : (
                <AccountAutoComplete
                  id={field.name}
                  defaultValue={field.value}
                  defaultId={field.value?.id}
                  className={classNames('w-full text-sm', {
                    'p-invalid': fieldState.invalid,
                  })}
                  onSelect={(e: AutoCompleteSelectEvent) => {
                    field.onChange(e.value);
                  }}
                  appendTo={undefined}
                />
              )
            }
          />
          {getFormErrorMessage('freezeOwnerId')}
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">Breed</label>
          <Controller
            name="breedId"
            control={control}
            render={({ field, fieldState }) => (
              <BreedsAutoComplete
                defaultValue={field.value as any}
                onSelect={(e: AutoCompleteSelectEvent) => {
                  field.onChange(e.value);
                }}
                className={classNames('w-full text-sm', {
                  'p-invalid': fieldState.invalid,
                })}
                appendTo={undefined}
              />
            )}
          />
          {getFormErrorMessage('breedId')}
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">Method</label>
          <Controller
            name="method"
            control={control}
            render={({ field }) => (
              <Dropdown
                id={field.name}
                value={field.value}
                options={EMBRYO_METHOD_OPTIONS}
                className="w-full text-sm"
                onChange={(e) => field.onChange(e.value)}
                placeholder="Select method"
                appendTo={undefined}
              />
            )}
          />
          {getFormErrorMessage('method')}
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">
            Recovery Firm Code
          </label>
          <Controller
            name="recoveryFirmCode"
            control={control}
            render={({ field }) => (
              <InputText
                id={field.name}
                value={field.value || ''}
                className="w-full text-sm"
                onChange={(e) => field.onChange(e.target.value)}
                placeholder="Enter recovery firm code"
              />
            )}
          />
          {getFormErrorMessage('recoveryFirmCode')}
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">
            Recovery Firm Name
          </label>
          <Controller
            name="recoveryFirmName"
            control={control}
            render={({ field }) => (
              <InputText
                id={field.name}
                value={field.value || ''}
                className="w-full text-sm"
                onChange={(e) => field.onChange(e.target.value)}
                placeholder="Enter recovery firm name"
              />
            )}
          />
          {getFormErrorMessage('recoveryFirmName')}
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">
            <div className="flex items-center gap-1">
              <span>Fresh Frozen</span>
              <span className="text-red-500">*</span>
            </div>
          </label>
          <Controller
            name="freshFrozen"
            control={control}
            render={({ field, fieldState }) => (
              <Dropdown
                id={field.name}
                value={field.value}
                options={FRESH_FROZEN_OPTIONS}
                className={classNames('w-full text-sm', {
                  'p-invalid': fieldState.invalid,
                })}
                onChange={(e) => field.onChange(e.value)}
                placeholder="Select"
                appendTo={undefined}
              />
            )}
          />
          {getFormErrorMessage('freshFrozen')}
        </div>

        {!isOutsideEmbryoUpdatesEnabled && (
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">Cane</label>
            <Controller
              name="freezeCane"
              control={control}
              render={({ field }) => (
                <InputText
                  id={field.name}
                  value={field.value || ''}
                  className="w-full text-sm"
                  onChange={(e) => field.onChange(e.target.value)}
                  placeholder="Enter cane"
                />
              )}
            />
            {getFormErrorMessage('freezeCane')}
          </div>
        )}

        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">
            Creation/Freeze Date
          </label>
          <Controller
            name="creation"
            control={control}
            render={({ field, fieldState }) => (
              <Calendar
                id={field.name}
                value={field.value}
                className={classNames('w-full', {
                  'p-invalid': fieldState.invalid,
                })}
                onChange={(e) => field.onChange(e.value)}
                showIcon
                dateFormat="mm/dd/yy"
                showOnFocus={false}
                placeholder="mm/dd/yyyy"
              />
            )}
          />
          {getFormErrorMessage('creation')}
        </div>

        {!isOutsideEmbryoUpdatesEnabled && (
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">
              Freeze Method
            </label>
            <Controller
              name="freezeMethodId"
              control={control}
              render={({ field }) => (
                <OptionSetsDropdown
                  label={undefined}
                  value={field.value}
                  placeholder="Select method"
                  optionSetsName="Freezing Methods"
                  onChange={(e) => field.onChange(e.value)}
                  className="w-full text-sm"
                  appendTo={undefined}
                />
              )}
            />
            {getFormErrorMessage('freezeMethodId')}
          </div>
        )}
      </div>
    </div>
  );

  const renderTransfersStep = () => (
    <div className="bg-white p-6">
      <h3 className="mb-6 text-lg font-bold text-blue-600">Transfer Details</h3>
      <div className="grid grid-cols-3 gap-6">
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">
            <div className="flex items-center gap-1">
              <span>Implant Date</span>
              <span className="text-red-500">*</span>
            </div>
          </label>
          <Controller
            name="implantDate"
            control={control}
            render={({ field, fieldState }) => (
              <Calendar
                id={field.name}
                value={field.value}
                className={classNames('w-full', {
                  'p-invalid': fieldState.invalid,
                })}
                onChange={(e) => field.onChange(e.value)}
                showIcon
                dateFormat="mm/dd/yy"
                showOnFocus={false}
                placeholder="mm/dd/yyyy"
              />
            )}
          />
          {getFormErrorMessage('implantDate')}
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">
            Transfer Quantity
          </label>
          <Controller
            name="transferQuantity"
            control={control}
            render={({ field }) => (
              <InputNumber
                locale="en-US"
                value={field.value ?? 0}
                onValueChange={(e) => field.onChange(e.value)}
                placeholder="IVF Number"
                invalid={!!errors.transferQuantity}
                inputId="transferQuantity"
                readOnly
                className="w-full"
                inputClassName="w-full"
                disabled
              />
            )}
          />
          {getFormErrorMessage('transferQuantity')}
        </div>

        {!isOutsideEmbryoUpdatesEnabled && (
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">
              Thaw By Name
            </label>
            <Controller
              name="thawByName"
              control={control}
              render={({ field }) => (
                <InputText
                  id={field.name}
                  value={typeof field.value === 'string' ? field.value : ''}
                  className="w-full text-sm"
                  onChange={(e) => field.onChange(e.target.value || undefined)}
                  placeholder="Enter name"
                />
              )}
            />
            {getFormErrorMessage('thawByName')}
          </div>
        )}

        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">
            Implant Tier Of
          </label>
          <Controller
            name="implantTierOf"
            control={control}
            render={({ field }) => (
              <InputNumber
                locale="en-US"
                value={field.value ?? 0}
                onValueChange={(e) => field.onChange(e.value)}
                placeholder="Enter tier"
                invalid={!!errors.implantTierOf}
                inputId="implantTierOf"
              />
            )}
          />
          {getFormErrorMessage('implantTierOf')}
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">
            Load Donor Partners
          </label>
          <Controller
            name="loadDonorPartners"
            control={control}
            render={({ field, fieldState }) => (
              <Dropdown
                id={field.name}
                value={field.value}
                options={LOAD_DONOR_PARTNERS_OPTIONS}
                className={classNames('w-full text-sm', {
                  'p-invalid': fieldState.invalid,
                })}
                onChange={(e) => field.onChange(e.value)}
                placeholder="Select"
                appendTo={undefined}
              />
            )}
          />
          {getFormErrorMessage('loadDonorPartners')}
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">
            <div className="flex items-center gap-1">
              <span>Transfer Site</span>
              <span className="text-red-500">*</span>
            </div>
          </label>
          <Controller
            name="transferSiteId"
            control={control}
            render={({ field, fieldState }) => (
              <SitesAutoComplete
                label={undefined}
                defaultValue={field.value}
                onSelect={(e: AutoCompleteSelectEvent) => {
                  field.onChange(e.value);
                }}
                className={classNames('w-full text-sm', {
                  'p-invalid': fieldState.invalid,
                })}
                appendTo={undefined}
              />
            )}
          />
          {getFormErrorMessage('transferSiteId')}
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">
            <div className="flex items-center gap-1">
              <span>Transfer Clinic Ranch Haul In</span>
              <span className="text-red-500">*</span>
            </div>
          </label>
          <Controller
            name="transferClinicRanchHaulIn"
            control={control}
            render={({ field, fieldState }) => (
              <Dropdown
                id={field.name}
                value={field.value}
                options={TRANSFER_CLINIC_RANCH_HAULIN_OPTIONS}
                className={classNames('w-full text-sm', {
                  'p-invalid': fieldState.invalid,
                })}
                onChange={(e) => field.onChange(e.value)}
                placeholder="Select"
                appendTo={undefined}
              />
            )}
          />
          {getFormErrorMessage('transferClinicRanchHaulIn')}
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">
            Transfer Tech
          </label>
          <Controller
            name="transferTechName"
            control={control}
            render={({ field }) => (
              <InputText
                id={field.name}
                value={field.value || ''}
                className="w-full text-sm"
                onChange={(e) => field.onChange(e.target.value)}
                placeholder="Enter transfer tech name"
              />
            )}
          />
          {getFormErrorMessage('transferTechName')}
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">
            Implant Type
          </label>
          <Controller
            name="implantType"
            control={control}
            render={({ field, fieldState }) => (
              <StringMapDropdown
                appendTo={undefined}
                value={field.value || undefined}
                objectTypeCode={StringMapObjectTypeCode.IMPLANT_TYPE}
                attributeName="tog_implantType"
                className={classNames('w-full text-sm', {
                  'p-invalid': fieldState.invalid,
                })}
                onChange={(e: DropdownChangeEvent) => field.onChange(e.value)}
              />
            )}
          />
          {getFormErrorMessage('implantType')}
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">
            Recipient Owner
          </label>
          <Controller
            name="recipientOwnerId"
            control={control}
            render={({ field, fieldState }) =>
              isEmbryoOwnerAlternateSearchEnabled ? (
                <AdvancedAccountLookup
                  value={field.value as AccountResponse | null}
                  className={classNames('w-full text-sm', {
                    'p-invalid': fieldState.invalid,
                  })}
                  onChange={(account) => field.onChange(account)}
                />
              ) : (
                <AccountAutoComplete
                  id={field.name}
                  defaultValue={field.value}
                  defaultId={field.value?.id}
                  className={classNames('w-full text-sm', {
                    'p-invalid': fieldState.invalid,
                  })}
                  onSelect={(e: AutoCompleteSelectEvent) => {
                    field.onChange(e.value);
                  }}
                  appendTo={undefined}
                />
              )
            }
          />
          {getFormErrorMessage('recipientOwnerId')}
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">Yard</label>
          <Controller
            name="deyardId"
            control={control}
            render={({ field, fieldState }) => (
              <YardsAutoComplete
                label={undefined}
                defaultValue={field.value || undefined}
                onSelect={(e: AutoCompleteSelectEvent) => {
                  field.onChange(e.value);
                }}
                className={classNames('w-full text-sm', {
                  'p-invalid': fieldState.invalid,
                })}
                appendTo={undefined}
              />
            )}
          />
          {getFormErrorMessage('deyardId')}
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">
            Transfer Owner
          </label>
          <Controller
            name="transferOwnerId"
            control={control}
            render={({ field, fieldState }) =>
              isEmbryoOwnerAlternateSearchEnabled ? (
                <AdvancedAccountLookup
                  value={field.value as AccountResponse | null}
                  className={classNames('w-full text-sm', {
                    'p-invalid': fieldState.invalid,
                  })}
                  onChange={(account) => field.onChange(account)}
                />
              ) : (
                <AccountAutoComplete
                  id={field.name}
                  defaultValue={field.value}
                  defaultId={field.value?.id}
                  className={classNames('w-full text-sm', {
                    'p-invalid': fieldState.invalid,
                  })}
                  onSelect={(e: AutoCompleteSelectEvent) => {
                    field.onChange(e.value);
                  }}
                  appendTo={undefined}
                />
              )
            }
          />
          {getFormErrorMessage('transferOwnerId')}
        </div>
      </div>

      <div className="mt-6">
        <h4 className="mb-2 text-sm font-semibold text-gray-700">
          Implanted Embryo Grading
        </h4>
        <GradingGrid
          rowData={implantGradingGridData}
          storageKey="outside-embryos-implant-grading-grid"
          onCellValueChanged={(updatedData) => {
            setImplantGradingGridData(updatedData);
            setValue('transferQuantity', sumGradingRows(updatedData));
          }}
        />
      </div>
    </div>
  );

  const renderFreezingStep = () => {
    if (isFresh) {
      return (
        <div className="bg-white p-6">
          <h3 className="mb-6 text-lg font-bold text-blue-600">
            Freezing Information
          </h3>
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-6 text-amber-800">
            <p className="font-medium">
              Freezing Information does not apply to fresh embryos.
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="bg-white p-6">
        <h3 className="mb-6 text-lg font-bold text-blue-600">
          {isOutsideEmbryoUpdatesEnabled
            ? 'Frozen Information'
            : 'Freezing Information'}
        </h3>
        {isOutsideEmbryoUpdatesEnabled && (
          <div className="mb-4 flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
            <i className="pi pi-info-circle mt-0.5" />
            <span>
              <strong>Cane</strong> and <strong>Storage Location</strong> are
              required for the Create Embryo workflow to run on freeze entries.
            </span>
          </div>
        )}
        <div className="grid grid-cols-3 gap-8">
          {isOutsideEmbryoUpdatesEnabled && (
            <>
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-gray-700">
                  Cane <span className="text-red-500">*</span>
                </label>
                <Controller
                  name="freezeCane"
                  control={control}
                  render={({ field }) => (
                    <InputText
                      id={field.name}
                      value={field.value || ''}
                      className="w-full text-sm"
                      onChange={(e) => field.onChange(e.target.value)}
                      placeholder="Enter cane"
                    />
                  )}
                />
                {getFormErrorMessage('freezeCane')}
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-gray-700">
                  Freeze Method
                </label>
                <Controller
                  name="freezeMethodId"
                  control={control}
                  render={({ field }) => (
                    <OptionSetsDropdown
                      label={undefined}
                      value={field.value}
                      placeholder="Select method"
                      optionSetsName="Freezing Methods"
                      onChange={(e) => field.onChange(e.value)}
                      className="w-full text-sm"
                      appendTo={undefined}
                    />
                  )}
                />
                {getFormErrorMessage('freezeMethodId')}
              </div>
            </>
          )}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">
              Freeze Quantity
            </label>
            <Controller
              name="freezeQuantity"
              control={control}
              render={({ field }) => (
                <InputNumber
                  locale="en-US"
                  value={field.value ?? 0}
                  onValueChange={(e) => field.onChange(e.value)}
                  placeholder="Enter quantity"
                  invalid={!!errors.ivfNumber}
                  inputId="freezeQuantity"
                  readOnly
                  className="w-full"
                  inputClassName="w-full"
                  disabled
                />
              )}
            />
            {getFormErrorMessage('freezeQuantity')}
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">
              Freeze Storage Location
              {isOutsideEmbryoUpdatesEnabled && (
                <span className="text-red-500">*</span>
              )}
            </label>
            <Controller
              name="freezeStorageLocationId"
              control={control}
              render={({ field, fieldState }) =>
                isStorageLocationPagingEnabled ? (
                  <StorageLocationCombobox
                    value={(field.value as StorageLocation) || null}
                    onChange={field.onChange}
                    className={classNames('w-full text-sm', {
                      'p-invalid': fieldState.invalid,
                    })}
                    pagingEnabled
                  />
                ) : (
                  <StorageLocationAutoComplete
                    defaultValue={(field.value as StorageLocation) || undefined}
                    onSelect={(e: AutoCompleteSelectEvent) => {
                      field.onChange(e.value);
                    }}
                    className={classNames('w-full text-sm', {
                      'p-invalid': fieldState.invalid,
                    })}
                    appendTo={undefined}
                  />
                )
              }
            />
            {getFormErrorMessage('freezeStorageLocationId')}
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">
              Freeze Tech
            </label>
            <Controller
              name="freezeTechName"
              control={control}
              render={({ field }) => (
                <InputText
                  id={field.name}
                  value={field.value || ''}
                  className="w-full text-sm"
                  onChange={(e) => field.onChange(e.target.value)}
                  placeholder="Enter freeze tech"
                />
              )}
            />
            {getFormErrorMessage('freezeTechName')}
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">
              Freeze TT
            </label>
            <Controller
              name="freezeTt"
              control={control}
              render={({ field }) => (
                <Dropdown
                  id={field.name}
                  value={field.value}
                  options={[
                    { label: 'Yes', value: true },
                    { label: 'No', value: false },
                  ]}
                  className="w-full text-sm"
                  onChange={(e) => field.onChange(e.value)}
                  placeholder="Select"
                />
              )}
            />
            {getFormErrorMessage('freezeTt')}
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">
              Received On
            </label>
            <Controller
              name="receivedOn"
              control={control}
              render={({ field, fieldState }) => (
                <Calendar
                  id={field.name}
                  value={field.value}
                  className={classNames('w-full', {
                    'p-invalid': fieldState.invalid,
                  })}
                  onChange={(e) => field.onChange(e.value)}
                  showIcon
                  dateFormat="mm/dd/yy"
                  showOnFocus={false}
                  placeholder="mm/dd/yyyy"
                />
              )}
            />
            {getFormErrorMessage('receivedOn')}
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">
              Freeze Time
            </label>
            <Controller
              name="freezeTime"
              control={control}
              render={({ field }) => (
                <InputText
                  id={field.name}
                  value={field.value || ''}
                  className="w-full text-sm"
                  onChange={(e) => field.onChange(e.target.value)}
                  placeholder="Enter freeze time"
                />
              )}
            />
            {getFormErrorMessage('freezeTime')}
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">Freezer</label>
            <Controller
              name="freezer"
              control={control}
              render={({ field }) => (
                <InputText
                  id={field.name}
                  value={field.value || ''}
                  className="w-full text-sm"
                  onChange={(e) => field.onChange(e.target.value)}
                  placeholder="Enter freezer"
                />
              )}
            />
            {getFormErrorMessage('freezer')}
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">Sex</label>

            <Controller
              name="sex"
              control={control}
              render={({ field }) => (
                <Dropdown
                  id={field.name}
                  value={field.value || ''}
                  className="w-full text-sm"
                  onChange={(e) => field.onChange(e.value)}
                  placeholder="Enter sex"
                  options={sexOptions}
                  appendTo={undefined}
                />
              )}
            />
            {getFormErrorMessage('sex')}
          </div>
        </div>
        <div className="freeze-grading-section col-span-2">
          <h4 className="mb-2 text-sm font-semibold text-gray-700">
            Freeze Embryo Grading
          </h4>
          <GradingGrid
            rowData={gradingGridData}
            storageKey="outside-embryos-grading-grid"
            onCellValueChanged={(updatedData) => {
              setGradingGridData(updatedData);
              setValue('freezeQuantity', sumGradingRows(updatedData));
            }}
          />
        </div>
      </div>
    );
  };

  const handleFormKeyDown = (e: KeyboardEvent<HTMLFormElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  const handleSave = () => {
    handleSubmit(onSubmit)();
  };

  const handleFormSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
  };

  return (
    <form
      onSubmit={handleFormSubmit}
      onKeyDown={handleFormKeyDown}
      className="flex h-full flex-col"
    >
      <div className="mb-6">
        <Steps model={steps} activeIndex={activeIndex} />
      </div>
      <div className="flex-1 overflow-auto">
        <div
          className={activeIndex === 0 ? 'block' : 'hidden'}
          aria-hidden={activeIndex !== 0}
        >
          {renderGeneralStep()}
        </div>
        <div
          className={activeIndex === 1 ? 'block' : 'hidden'}
          aria-hidden={activeIndex !== 1}
        >
          {renderTransfersStep()}
        </div>
        <div
          className={activeIndex === 2 ? 'block' : 'hidden'}
          aria-hidden={activeIndex !== 2}
        >
          {renderFreezingStep()}
        </div>
      </div>
      <div className="flex justify-between gap-3 border-t border-gray-200 bg-white p-4">
        <Button
          label={t('common.cancel')}
          icon="pi pi-times"
          severity="secondary"
          onClick={handleCancel}
          type="button"
          disabled={isSubmitting}
        />
        <div className="flex gap-3">
          {activeIndex > 0 && (
            <Button
              label={t('common.back')}
              icon="pi pi-arrow-left"
              severity="secondary"
              onClick={handleBack}
              type="button"
              disabled={isSubmitting}
              aria-label={t('common.back')}
            />
          )}
          {activeIndex < steps.length - 1 ? (
            <Button
              label={t('common.next')}
              icon="pi pi-arrow-right"
              iconPos="right"
              onClick={handleNext}
              type="button"
              disabled={isSubmitting}
            />
          ) : (
            <Button
              label={t('common.save')}
              icon="pi pi-check"
              type="button"
              onClick={handleSave}
              loading={isSubmitting}
            />
          )}
        </div>
      </div>
    </form>
  );
};
